import classNames from 'classnames'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { getAccessibilityTag } from '../../../config'
import { IconButton, IconButtonSize, IconButtonVariants } from '../../button'
import { CrossIcon } from '../../icon'
import { Scrim } from '../internal'
import styles from './BaseModal.module.scss'
import BaseModalTopArea from './BaseModalTopArea'
import type { BaseModalHandle, BaseModalProps} from './types'
import { BaseModalSize } from './types'

const Modal = forwardRef(function Modal(
  {
    'aria-labelledby': ariaLabelledBy,
    closeButtonAriaLabel,
    id,
    isOpen: openProp,
    onClose,
    overlayClose = true,
    fullscreen = false,
    showCloseButton = true,
    size = BaseModalSize.medium,
    children,
    className,
    withScrim = true,
    dragHandler,
    role,
  }: BaseModalProps,
  ref
) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  //eslint-disable-next-line
  const backdropRef = useRef<any>(null)
  const onCloseTimeoutRef = useRef<NodeJS.Timeout>()
  useImperativeHandle(
    ref,
    (): BaseModalHandle => ({
      getDialogRef() {
        return dialogRef
      },
    }),
    []
  )

  const closeModal = useCallback(() => {
    onCloseTimeoutRef.current && clearTimeout(onCloseTimeoutRef.current)
    //set the hiding class on the dialog
    dialogRef.current?.classList.add(styles['pintxo-modal--closing'])
    //imperatively start the closing animation on the backdrop
    backdropRef.current?.startClosing()
    //wait for the animation to finish
    onCloseTimeoutRef.current = setTimeout(() => {
      onClose && onClose()
    }, 480)
  }, [onClose])

  useEffect(() => {
    onCloseTimeoutRef.current && clearTimeout(onCloseTimeoutRef.current)
  }, [])

  const handleOverlayClick = useCallback(() => {
    if (overlayClose) {
      closeModal()
    }
  }, [closeModal, overlayClose])

  useEffect(() => {
    const handleKeyDownEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal()
      }
    }

    if (openProp) {
      window.addEventListener('keydown', handleKeyDownEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDownEscape)
    }
  }, [closeModal, openProp])

  const onDialogClick = useCallback((e: React.MouseEvent<HTMLElement>) => e.stopPropagation(), [])

  const closeAriaLabel = getAccessibilityTag('close')

  const Dialog = (
    <dialog
      ref={dialogRef}
      aria-label="dialog"
      className={classNames(styles['pintxo-modal'], className)}
      data-size={size}
      data-fullscreen={fullscreen}
      tabIndex={0}
      onClick={onDialogClick}
      {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
      {...(role ? { role: role } : {})}
      {...(id ? { id } : {})}
    >
      <div className={classNames(styles['pintxo-modal__top-area'])}>
        {dragHandler && dragHandler}
        <BaseModalTopArea />
      </div>
      <div className={styles['pintxo-modal__container']}>
        <div className={styles['pintxo-modal__container__header-empty']}></div>
        {showCloseButton && (
          <div className={styles['pintxo-modal__close-container']}>
            <IconButton
              aria-label={closeButtonAriaLabel || closeAriaLabel}
              variant={IconButtonVariants.neutral}
              size={IconButtonSize.small}
              onClick={closeModal}
              icon={CrossIcon}
            />
          </div>
        )}
        {children}
      </div>
      <div className={styles['pintxo-modal__unsafe-area']}></div>
    </dialog>
  )

  if (!openProp) return null

  return withScrim ? (
    <Scrim onClick={handleOverlayClick} ref={backdropRef}>
      {Dialog}
    </Scrim>
  ) : (
    Dialog
  )
})

export default Modal
